import { useUnsavedChanges } from '@/hooks/useUnsavedChanges';
import { useEffect, useState } from 'react';
import type { CardExtraction, ExLayer, ExWaterLevel, ExCasing, ExPumping, ExWaterValue, ExWaterSample } from './cardTypes';

/**
 * Okno zatwierdzenia odczytu AI: pokazuje dane wyciągnięte z dokumentu/zdjęcia/arkusza,
 * pozwala je poprawić (pola skalarne + kolekcje: warstwy, zwierciadła, konstrukcja, pompowania,
 * badanie wody) i dopiero po „Zatwierdź" scala je w kartę. Nic nie zapisuje samodzielnie.
 */

const num = (s: string): number | null => {
  const t = s.trim().replace(',', '.');
  if (t === '') return null;
  const n = Number(t);
  return Number.isFinite(n) ? n : null;
};
const fmtNum = (v: number | null | undefined): string => (v === null || v === undefined ? '' : String(v).replace('.', ','));

const CASING_KIND_OPTS = [
  { value: 'DRILL_BORE', label: 'Przewiert' },
  { value: 'CASING_PIPE', label: 'Rura okładzinowa' },
  { value: 'OVER_FILTER_PIPE', label: 'Rura nadfiltrowa' },
  { value: 'FILTER', label: 'Filtr' },
  { value: 'UNDER_FILTER_PIPE', label: 'Rura podfiltrowa' },
  { value: 'GRAVEL_PACK', label: 'Obsypka żwirowa' },
  { value: 'CLAY_PLUG', label: 'Korek iłowy' },
  { value: 'SEAL', label: 'Zamek / uszczelka' },
  { value: 'CEMENTATION', label: 'Cementacja' },
  { value: 'SUMP', label: 'Rura-osadnik' },
  { value: 'BOTTOM_CAP', label: 'Denko' },
];
const WL_KIND_OPTS = [
  { value: 'DRILLED', label: 'Nawiercone' },
  { value: 'STABILIZED', label: 'Ustabilizowane' },
];

const emptyLayer = (): ExLayer => ({ fromDepth: null, toDepth: null, symbol: null, description: null, stratigraphy: null, soilCategory: null });
const emptyWl = (): ExWaterLevel => ({ kind: 'STABILIZED', depthMPpt: null, measuredDate: null });
const emptyCasing = (): ExCasing => ({ kind: 'CASING_PIPE', fromDepth: null, toDepth: null, outerDiameterMm: null, diameterInch: null, material: null, meshType: null, fraction: null });
const emptyPumping = (): ExPumping => ({ stageNo: null, qM3h: null, drawdownSM: null, durationH: null, specificCapacity: null });
const emptyWaterVal = (): ExWaterValue => ({ paramName: null, unit: null, valueText: null });
const emptyWaterSample = (): ExWaterSample => ({ sampledDate: null, labName: null, values: [] });

// ---------- styles ----------
const overlay: React.CSSProperties = { position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.55)', zIndex: 80, display: 'flex', padding: 16, alignItems: 'center', justifyContent: 'center' };
const panel: React.CSSProperties = { background: '#fff', borderRadius: 10, width: 'min(940px, 100%)', maxHeight: '92vh', display: 'flex', flexDirection: 'column', overflow: 'hidden', boxShadow: '0 20px 60px rgba(15,23,42,0.35)' };
const inp: React.CSSProperties = { fontSize: 12, padding: '6px 7px', border: '1px solid #cbd5e1', borderRadius: 4, color: '#0f172a', width: '100%', boxSizing: 'border-box', background: '#fff' };
const grid = (min = 150): React.CSSProperties => ({ display: 'grid', gridTemplateColumns: `repeat(auto-fill, minmax(${min}px, 1fr))`, gap: 10 });
const xbtn: React.CSSProperties = { background: '#fee2e2', color: '#b91c1c', border: 'none', borderRadius: 4, width: 24, height: 24, cursor: 'pointer', flex: 'none', alignSelf: 'flex-start' };
const addBtn: React.CSSProperties = { background: '#e0f2fe', color: '#075985', border: 'none', borderRadius: 4, padding: '5px 10px', fontSize: 12, cursor: 'pointer', marginTop: 6 };
const rowBox: React.CSSProperties = { border: '1px solid #eef2f7', borderRadius: 6, padding: 8, marginBottom: 8, display: 'flex', gap: 8, alignItems: 'stretch' };

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label style={{ display: 'flex', flexDirection: 'column', gap: 3, fontSize: 10.5, color: '#64748b', minWidth: 0 }}>
      <span style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{label}</span>
      {children}
    </label>
  );
}
function TextInput({ label, value, onChange, area }: { label: string; value: string | null; onChange: (v: string | null) => void; area?: boolean }) {
  return (
    <Field label={label}>
      {area
        ? <textarea rows={2} value={value ?? ''} onChange={(e) => onChange(e.target.value === '' ? null : e.target.value)} style={{ ...inp, resize: 'vertical' }} />
        : <input value={value ?? ''} onChange={(e) => onChange(e.target.value === '' ? null : e.target.value)} style={inp} />}
    </Field>
  );
}
/** Pole liczbowe: lokalny string podczas pisania (nie gubi przecinka), resync do wartości po utracie fokusu. */
function NumInput({ label, value, onChange }: { label: string; value: number | null; onChange: (v: number | null) => void }) {
  const [s, setS] = useState(() => fmtNum(value));
  const [focused, setFocused] = useState(false);
  useEffect(() => { if (!focused) setS(fmtNum(value)); }, [value, focused]);
  return (
    <Field label={label}>
      <input inputMode="decimal" value={s} style={inp}
        onFocus={() => setFocused(true)}
        onBlur={() => setFocused(false)}
        onChange={(e) => { setS(e.target.value); onChange(num(e.target.value)); }} />
    </Field>
  );
}
function DateInput({ label, value, onChange }: { label: string; value: string | null; onChange: (v: string | null) => void }) {
  return (
    <Field label={label}>
      <input type="date" value={value ?? ''} onChange={(e) => onChange(e.target.value || null)} style={inp} />
    </Field>
  );
}
function SelectInput({ label, value, onChange, options }: { label: string; value: string | null; onChange: (v: string) => void; options: Array<{ value: string; label: string }> }) {
  return (
    <Field label={label}>
      <select value={value ?? ''} onChange={(e) => onChange(e.target.value)} style={inp}>
        {options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
      </select>
    </Field>
  );
}
function SectionTitle({ children, count }: { children: React.ReactNode; count?: number }) {
  return (
    <div style={{ fontSize: 13, fontWeight: 600, color: '#0f172a', margin: '16px 0 8px', display: 'flex', alignItems: 'center', gap: 8 }}>
      {children}
      {count !== undefined && <span style={{ fontSize: 11, fontWeight: 500, color: '#64748b' }}>({count})</span>}
    </div>
  );
}

interface Props {
  extraction: CardExtraction;
  onCancel: () => void;
  onConfirm: (edited: CardExtraction) => void;
}

export function ExtractionReviewModal({ extraction, onCancel, onConfirm }: Props) {
  const [d, setD] = useState<CardExtraction>(() => JSON.parse(JSON.stringify(extraction)) as CardExtraction);

  const { requestClose, confirmation, dialogProps } = useUnsavedChanges({ value: d, onClose: onCancel });

  const set = (patch: Partial<CardExtraction>) => setD((prev) => ({ ...prev, ...patch }));
  const setLayer = (i: number, patch: Partial<ExLayer>) => setD((p) => ({ ...p, layers: p.layers.map((l, x) => (x === i ? { ...l, ...patch } : l)) }));
  const setWl = (i: number, patch: Partial<ExWaterLevel>) => setD((p) => ({ ...p, waterLevels: p.waterLevels.map((l, x) => (x === i ? { ...l, ...patch } : l)) }));
  const setCasing = (i: number, patch: Partial<ExCasing>) => setD((p) => ({ ...p, casing: p.casing.map((l, x) => (x === i ? { ...l, ...patch } : l)) }));
  const setPumping = (i: number, patch: Partial<ExPumping>) => setD((p) => ({ ...p, pumping: p.pumping.map((l, x) => (x === i ? { ...l, ...patch } : l)) }));
  const setWater = (patch: Partial<ExWaterSample>) => setD((p) => ({ ...p, water: { ...(p.water ?? emptyWaterSample()), ...patch } }));
  const setWaterVal = (i: number, patch: Partial<ExWaterValue>) => setD((p) => (p.water ? { ...p, water: { ...p.water, values: p.water.values.map((v, x) => (x === i ? { ...v, ...patch } : v)) } } : p));

  const provider = d.provider ?? '—';
  const isMock = (d.provider ?? '').toLowerCase() === 'mock';
  const foundCount = d.extractedFields?.length ?? 0;

  return (
    <div style={overlay} {...dialogProps} onMouseDown={(e) => { if (e.target === e.currentTarget) requestClose(); }}>
      {confirmation}
      <div style={panel}>
        {/* header */}
        <div style={{ padding: '12px 16px', borderBottom: '1px solid #e2e8f0' }}>
          <div style={{ fontSize: 15, fontWeight: 600, color: '#0f172a' }}>✦ Odczytane dane — sprawdź i zatwierdź</div>
          <div style={{ fontSize: 12, color: '#64748b', marginTop: 2 }}>
            Źródło: AI ({provider}{d.model ? ` · ${d.model}` : ''}) · znaleziono {foundCount} pól. Popraw wartości, a następnie „Zatwierdź".
          </div>
        </div>

        {/* body */}
        <div style={{ padding: 16, overflow: 'auto' }}>
          {isMock && (
            <div style={{ background: '#fef9c3', border: '1px solid #fde68a', color: '#854d0e', borderRadius: 6, padding: '8px 10px', fontSize: 12, marginBottom: 12 }}>
              ⚠ AI działa w trybie <b>MOCK</b> — to nie jest odczyt Twojego pliku, tylko dane przykładowe.
              Ustaw providera Claude/Gemini + klucz API w <b>/admin/config</b>, aby czytać z realnego dokumentu.
            </div>
          )}

          <SectionTitle>Dane otworu</SectionTitle>
          <div style={grid()}>
            <NumInput label="Rzędna [m n.p.m.]" value={d.elevationMNpm} onChange={(v) => set({ elevationMNpm: v })} />
            <DateInput label="Rozpoczęcie robót" value={d.workStartDate} onChange={(v) => set({ workStartDate: v })} />
            <DateInput label="Zakończenie robót" value={d.workEndDate} onChange={(v) => set({ workEndDate: v })} />
            <NumInput label="Głębokość całk. [m]" value={d.totalDepth} onChange={(v) => set({ totalDepth: v })} />
            <NumInput label="Średnica [mm]" value={d.diameterMm} onChange={(v) => set({ diameterMm: v })} />
            <NumInput label="Wydajność [m³/h]" value={d.yieldM3h} onChange={(v) => set({ yieldM3h: v })} />
            <NumInput label="Rok wykonania" value={d.yearCompleted} onChange={(v) => set({ yearCompleted: v })} />
          </div>
          <div style={{ ...grid(220), marginTop: 10 }}>
            <TextInput label="System / sposób wiercenia" value={d.drillingSystem} onChange={(v) => set({ drillingSystem: v })} />
            <TextInput label="Narzędzia wiertnicze" value={d.drillingTools} onChange={(v) => set({ drillingTools: v })} />
            <TextInput label="Sposób pobierania próbek" value={d.sampleMethod} onChange={(v) => set({ sampleMethod: v })} />
            <TextInput label="Przechowywanie próbek" value={d.sampleStorage} onChange={(v) => set({ sampleStorage: v })} />
          </div>
          <div style={{ marginTop: 10 }}>
            <TextInput label="Przebieg robót wiertniczych" value={d.drillingCourse} onChange={(v) => set({ drillingCourse: v })} area />
          </div>

          <SectionTitle>Obliczenia hydrogeologiczne</SectionTitle>
          <div style={grid()}>
            <NumInput label="k [m/s]" value={d.kValueMs} onChange={(v) => set({ kValueMs: v })} />
            <TextInput label="Wzór k" value={d.kFormula} onChange={(v) => set({ kFormula: v })} />
            <NumInput label="Zasięg R [m]" value={d.rRangeM} onChange={(v) => set({ rRangeM: v })} />
            <TextInput label="Wzór R" value={d.rFormula} onChange={(v) => set({ rFormula: v })} />
            <NumInput label="Q eksploat. [m³/h]" value={d.qExploatacyjneM3h} onChange={(v) => set({ qExploatacyjneM3h: v })} />
            <NumInput label="Q dop. filtra [m³/h]" value={d.qDopFiltraM3h} onChange={(v) => set({ qDopFiltraM3h: v })} />
            <NumInput label="Depresja Sc [m]" value={d.scTotalM} onChange={(v) => set({ scTotalM: v })} />
          </div>
          <div style={{ marginTop: 10 }}>
            <TextInput label="Geolog dokumentujący (nr upr.)" value={d.documentingGeologistText} onChange={(v) => set({ documentingGeologistText: v })} />
          </div>

          <SectionTitle count={d.layers.length}>Warstwy profilu</SectionTitle>
          {d.layers.map((l, i) => (
            <div key={i} style={rowBox}>
              <div style={{ ...grid(110), flex: 1 }}>
                <NumInput label="Od [m]" value={l.fromDepth} onChange={(v) => setLayer(i, { fromDepth: v })} />
                <NumInput label="Do [m]" value={l.toDepth} onChange={(v) => setLayer(i, { toDepth: v })} />
                <TextInput label="Symbol" value={l.symbol} onChange={(v) => setLayer(i, { symbol: v })} />
                <TextInput label="Opis litologii" value={l.description} onChange={(v) => setLayer(i, { description: v })} />
                <TextInput label="Stratygrafia" value={l.stratigraphy} onChange={(v) => setLayer(i, { stratigraphy: v })} />
                <TextInput label="Kat. gruntu" value={l.soilCategory} onChange={(v) => setLayer(i, { soilCategory: v })} />
              </div>
              <button title="Usuń warstwę" style={xbtn} onClick={() => setD((p) => ({ ...p, layers: p.layers.filter((_, x) => x !== i) }))}>✕</button>
            </div>
          ))}
          <button style={addBtn} onClick={() => setD((p) => ({ ...p, layers: [...p.layers, emptyLayer()] }))}>+ Dodaj warstwę</button>

          <SectionTitle count={d.waterLevels.length}>Zwierciadła wody</SectionTitle>
          {d.waterLevels.map((w, i) => (
            <div key={i} style={rowBox}>
              <div style={{ ...grid(140), flex: 1 }}>
                <SelectInput label="Rodzaj" value={w.kind} onChange={(v) => setWl(i, { kind: v })} options={WL_KIND_OPTS} />
                <NumInput label="Głębokość [m p.p.t.]" value={w.depthMPpt} onChange={(v) => setWl(i, { depthMPpt: v })} />
                <DateInput label="Data pomiaru" value={w.measuredDate} onChange={(v) => setWl(i, { measuredDate: v })} />
              </div>
              <button title="Usuń" style={xbtn} onClick={() => setD((p) => ({ ...p, waterLevels: p.waterLevels.filter((_, x) => x !== i) }))}>✕</button>
            </div>
          ))}
          <button style={addBtn} onClick={() => setD((p) => ({ ...p, waterLevels: [...p.waterLevels, emptyWl()] }))}>+ Dodaj zwierciadło</button>

          <SectionTitle count={d.casing.length}>Konstrukcja otworu</SectionTitle>
          {d.casing.map((c, i) => (
            <div key={i} style={rowBox}>
              <div style={{ ...grid(120), flex: 1 }}>
                <SelectInput label="Element" value={c.kind} onChange={(v) => setCasing(i, { kind: v })} options={CASING_KIND_OPTS} />
                <NumInput label="Od [m]" value={c.fromDepth} onChange={(v) => setCasing(i, { fromDepth: v })} />
                <NumInput label="Do [m]" value={c.toDepth} onChange={(v) => setCasing(i, { toDepth: v })} />
                <NumInput label="Ø zewn. [mm]" value={c.outerDiameterMm} onChange={(v) => setCasing(i, { outerDiameterMm: v })} />
                <TextInput label="Ø [cale]" value={c.diameterInch} onChange={(v) => setCasing(i, { diameterInch: v })} />
                <TextInput label="Materiał" value={c.material} onChange={(v) => setCasing(i, { material: v })} />
                <TextInput label="Rodzaj filtra" value={c.meshType} onChange={(v) => setCasing(i, { meshType: v })} />
                <TextInput label="Frakcja obsypki" value={c.fraction} onChange={(v) => setCasing(i, { fraction: v })} />
              </div>
              <button title="Usuń" style={xbtn} onClick={() => setD((p) => ({ ...p, casing: p.casing.filter((_, x) => x !== i) }))}>✕</button>
            </div>
          ))}
          <button style={addBtn} onClick={() => setD((p) => ({ ...p, casing: [...p.casing, emptyCasing()] }))}>+ Dodaj element</button>

          <SectionTitle count={d.pumping.length}>Pompowania</SectionTitle>
          {d.pumping.map((pu, i) => (
            <div key={i} style={rowBox}>
              <div style={{ ...grid(120), flex: 1 }}>
                <NumInput label="Stopień" value={pu.stageNo} onChange={(v) => setPumping(i, { stageNo: v })} />
                <NumInput label="Q [m³/h]" value={pu.qM3h} onChange={(v) => setPumping(i, { qM3h: v })} />
                <NumInput label="Depresja s [m]" value={pu.drawdownSM} onChange={(v) => setPumping(i, { drawdownSM: v })} />
                <NumInput label="Czas t [h]" value={pu.durationH} onChange={(v) => setPumping(i, { durationH: v })} />
                <NumInput label="q = Q/s" value={pu.specificCapacity} onChange={(v) => setPumping(i, { specificCapacity: v })} />
              </div>
              <button title="Usuń" style={xbtn} onClick={() => setD((p) => ({ ...p, pumping: p.pumping.filter((_, x) => x !== i) }))}>✕</button>
            </div>
          ))}
          <button style={addBtn} onClick={() => setD((p) => ({ ...p, pumping: [...p.pumping, emptyPumping()] }))}>+ Dodaj stopień</button>

          <SectionTitle count={d.water?.values.length}>Badanie wody</SectionTitle>
          {d.water ? (
            <div style={{ border: '1px solid #eef2f7', borderRadius: 6, padding: 10 }}>
              <div style={{ ...grid(180), marginBottom: 8 }}>
                <DateInput label="Data poboru" value={d.water.sampledDate} onChange={(v) => setWater({ sampledDate: v })} />
                <TextInput label="Laboratorium" value={d.water.labName} onChange={(v) => setWater({ labName: v })} />
              </div>
              {d.water.values.map((v, i) => (
                <div key={i} style={rowBox}>
                  <div style={{ ...grid(140), flex: 1 }}>
                    <TextInput label="Parametr" value={v.paramName} onChange={(x) => setWaterVal(i, { paramName: x })} />
                    <TextInput label="Jednostka" value={v.unit} onChange={(x) => setWaterVal(i, { unit: x })} />
                    <TextInput label="Wartość" value={v.valueText} onChange={(x) => setWaterVal(i, { valueText: x })} />
                  </div>
                  <button title="Usuń" style={xbtn} onClick={() => setD((p) => (p.water ? { ...p, water: { ...p.water, values: p.water.values.filter((_, x) => x !== i) } } : p))}>✕</button>
                </div>
              ))}
              <div style={{ display: 'flex', gap: 8 }}>
                <button style={addBtn} onClick={() => setD((p) => { const w = p.water ?? emptyWaterSample(); return { ...p, water: { ...w, values: [...w.values, emptyWaterVal()] } }; })}>+ Dodaj parametr</button>
                <button style={{ ...addBtn, background: '#fee2e2', color: '#b91c1c' }} onClick={() => set({ water: null })}>Usuń badanie wody</button>
              </div>
            </div>
          ) : (
            <button style={addBtn} onClick={() => set({ water: emptyWaterSample() })}>+ Dodaj badanie wody</button>
          )}

          {d.sourceTextPreview && (
            <details style={{ marginTop: 16 }}>
              <summary style={{ cursor: 'pointer', fontSize: 12, color: '#64748b' }}>Podgląd źródła (odczyt)</summary>
              <pre style={{ whiteSpace: 'pre-wrap', fontSize: 11, color: '#475569', background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 6, padding: 10, marginTop: 6, maxHeight: 200, overflow: 'auto' }}>{d.sourceTextPreview}</pre>
            </details>
          )}
        </div>

        {/* footer */}
        <div style={{ padding: '10px 16px', borderTop: '1px solid #e2e8f0', display: 'flex', justifyContent: 'flex-end', gap: 8, background: '#f8fafc' }}>
          <button onClick={requestClose} style={{ background: '#fff', color: '#475569', border: '1px solid #cbd5e1', borderRadius: 6, padding: '7px 14px', fontSize: 13, cursor: 'pointer' }}>Anuluj</button>
          <button onClick={() => onConfirm(d)} style={{ background: '#15803d', color: '#fff', border: 'none', borderRadius: 6, padding: '7px 14px', fontSize: 13, cursor: 'pointer' }}>✓ Zatwierdź i wypełnij kartę</button>
        </div>
      </div>
    </div>
  );
}
